/**
 * CB Core IAM Module
 * by Lev K. College Board 2016-2020
 */
import utils from './coreutils'

import {endsWith} from 'lodash-es';
import axios from 'axios';
import cookie from 'js-cookie';
import EventEmitter from 'wolfy87-eventemitter';
import { AwsV4Signer } from 'aws4fetch';
import Swal from 'sweetalert2';
import jwt_decode from "jwt-decode";

const CATAPULT_AUTHN_HEADER_NAME = 'x-cb-catapult-authentication-token';
const CATAPULT_AUTHZ_HEADER_NAME = 'x-cb-catapult-authorization-token';

  // event bus
  var eventBus;

  // levity api key (needed to call BSAuth to verify the SSO session)
  var bsauthPartialUrl =
    'https://bsauth-{{TREE-ENV}}.vpc.collegeboard.org/bsauth/rest/auth/me/cbssosession?api_key={{API-KEY}}&needProfile=Y';
  var cognitoPartialUrl =
    'https://sucred.catapult-{{PROD-NONPROD}}.collegeboard.org/rel/temp-user-aws-creds?cbEnv={{TREE-ENV}}';

  // requiresLogin will force the user to log in if she is not already
  var requiresLogin = false;
  var loginURI = '';

  // events
  var EVENT_NAME_PREFIX = 'cb:core:iam:events:';
  var events = Object.freeze({
    Login :  EVENT_NAME_PREFIX + 'Login',
    LoginExtended :  EVENT_NAME_PREFIX + 'LoginExtended', // new levity 6.5
    LoginFailed:  EVENT_NAME_PREFIX + 'LoginFailed',     // new levity 5.7+
    Logout : EVENT_NAME_PREFIX + 'Logout',
    AWSLogin :  EVENT_NAME_PREFIX + 'AWSLogin',     // new levity 4+
    AWSLoginRefresh: EVENT_NAME_PREFIX + 'AWSLoginRefresh',     // new levity 5.7+
    AWSLoginStarted :  EVENT_NAME_PREFIX + 'AWSLoginStarted',     // new levity 4+
    AWSLoginFinished:  EVENT_NAME_PREFIX + 'AWSLoginFinished',     // new levity 4+
    AWSLoginFailed:  EVENT_NAME_PREFIX + 'AWSLoginFailed',     // new levity 4+
    InvalidLocalClock:  EVENT_NAME_PREFIX + 'InvalidLocalClock',     // new levity 5+
    LegacyLoginLogout : 'widgets:custom:LoginLogout'   // legacy event for backward compatibility
  });

  const errorCodes = {
    Generic : 'Generic',
    RAOTTNotFound :  'RAOTTNotFound',
    RAOTTDisabled :  'RAOTTDisabled',
    RAOTTLoginTimeRange :  'RAOTTLoginTimeRange',
    RAOTTTooManyLogins : 'RAOTTTooManyLogins',
    RAOTTVerifierMismatch : 'RAOTTVerifierMismatch'
  };

  // cookie names
  var USER_COOKIE_NAME = {
    CBLOGIN: 'cb_login',
    LOWER_ENV: 'lower_Env',
    STUDENT_NAME: 'ecl_user_name',
    PRO_NAME: 'cb_pl_sso_user',
    STUDENT_CACHE_SESSION_ID: 'ECL_UT_NEW_LOGIN',
    PRO_CACHE_SESSION_ID: 'cb_pl_sso'
  };

  var DEFAULT_COOKIES_DOMAIN = '.collegeboard.org';
  var cookiesDomain = DEFAULT_COOKIES_DOMAIN;
  var loginTokenType = 'CBLogin';
  let loginTokenEnv = null;
  let isLoginRaottBased = false;
  let raott = null;
  let raottVerifierString = null;
  let areTempCredsNeeded = true;

  // browser session storage key to store/retrieve iam session
  var SESSION_STORAGE_IAM_KEY = "iamSession";

  var AWS_CREDS_KEY_PREFIX = "LevityTempAWSCreds_";
  var AWS_CREDS_SUBJECT_KEY_PREFIX = "LevityTempAWSCredsSubject";
  var AWS_CREDS_POOL_KEY_PREFIX = "LevityTempAWSCredsPool";

  // current state
  var currentSession = {};
  var currentLegacyState = {};

  // logger
  var logger = utils.Logger.getLogger('cb.core.iam');

  // session length in seconds
  var DEFAULT_SESSION_DURATION_IN_SECONDS = 7200; // 2 hours
  var sessionDurationInSecs = DEFAULT_SESSION_DURATION_IN_SECONDS;

  // sessionCheck over network, default true
  var sessionCheck = true;

  // max number of attempts to call BSAuth
  var MAX_N_OF_SESSION_CHECKS = 2;

  // number of attempts to call BSAuth
  var nOfSessionChecks = 0;

  // number of attempts to call catapult
  var nOfAWSAttempts = 0;

  var INVALID_INTERVAL_ID = -1;
  var INTERVAL_IN_MILLIS = 2000;
  var intervalID = INVALID_INTERVAL_ID;
  // 2.5 mins
  var INVALID_LOCAL_CLOCK_DIFF_IN_SECS = 2.5*60;

  var AWS_COGNITO_CB_DOMAIN = 'catapult';
  var AWS_COGNITO_USER_STATE_DATASET = 'user-session';
  var isAWS = false;
  var isAWSTempCredsRefreshInFlight = false;
  var cbAWSDomains = [];
  var forceAWSRefresh = false;

  // format of tempAWSCreds object:
  // tempAWSCreds = {
  //   cognitoIdentityPoolId : <cognitoPoolId>,
  //   subject : <cognitoUserId>,
  //   "<domain>" : {
  //      creds : {<creds>}
  //  }
  //};
  var tempAWSCreds = {
    cognitoIdentityPoolId : null,
    subject : null,
    jwtToken : null
  };

  var rootLogger = utils.Logger.getRootLogger();
  var lastErrorMsg = '';
  var isPartner = false;

  var showFatalAlerts = true;

  let isCatapultAuthOnly = false;
  let diffWithLocalInSecs = undefined;

  let sessionExtensionCheckInFlight = false;

const MAX_EXT_SESSION_DURATION_IN_MILLIS = 6 * 60 * 60 * 1000; // 6 hours  
const ACTIVITY_LOOK_BACK_IN_MILLIS = 5 * 60 * 1000; // 5 mins
const ACTIVITY_CHECK_BEFORE_EXP_IN_MILLIS = 15 * 1000; // 15 seconds
const ACTIVITY_DIALOG_MAX_DURATION_IN_MILLIS = 1 * 60 * 1000; // 1 min
const ACTIVITY_DIALOG_SHOW_TIME_BEFORE_EXP_IN_MILLIS = 
  ACTIVITY_CHECK_BEFORE_EXP_IN_MILLIS + ACTIVITY_DIALOG_MAX_DURATION_IN_MILLIS;
const ACTIVITY_DIALOG_LOOK_BACK_IN_MILLIS = ACTIVITY_LOOK_BACK_IN_MILLIS -
  ACTIVITY_DIALOG_MAX_DURATION_IN_MILLIS;

const ACTIVITY_INPUT_EVENTS = [
  'load', 'mousedown', 'click', 'keydown', 'touchstart'
];
const ACTIVITY_PLAY_START_EVENTS = [
  'play'
];
const ACTIVITY_PLAY_STOP_EVENTS = [
  'pause', 'ended', 'error'
];

let activityTracker = null;

function getActivityTrackerState(isDialog=false) {
  if (activityTracker)
    return activityTracker.getState(isDialog);
  else 
    return undefined;
}

class ActivityTracker {
  constructor(eventBus, logger) {
    if (!eventBus || !logger) {
      throw 'ActivityTracker requires valid levity event bus and logger';
    }

    this.eventBus = eventBus;
    this.logger = logger;
    this.lastUserInputTime = Date.now();
    this.isBeingTracked = false;
    this.curPlaying = 0;
    this.showDialogTimer = null;

    // subscribe to login and logout events
    this.eventBus.on(events.AWSLogin, this.onLogin.bind(this));
    this.eventBus.on(events.LoginExtended, this.onLogin.bind(this));
    this.eventBus.on(events.Logout, this.onLogout.bind(this));

    // start tracking media events once DOM is loaded
    executeAsEarlyAsPossible(this.startMediaTracking.bind(this));
  }

  clear() {
    // remove a dialog since it can be shown in multiple tabs
    if (Swal && Swal.isVisible())
      Swal.close();

    if (this.showDialogTimer)
      clearTimeout(this.showDialogTimer);
  }

  onLogin() {
    this.clear();
    
    // create a timer to show the dialog
    let delay = currentSession.expireTimeInMS - Date.now() - 
                ACTIVITY_DIALOG_SHOW_TIME_BEFORE_EXP_IN_MILLIS;
    //let delay = 2*60*1000; // TEST only
    this.logger.debug(
      `levity AT: inactivity dialog may be shown in ${Math.floor(delay/1000/60)} minutes`);
    this.showDialogTimer = setTimeout (this.showDialogIfNeeded.bind(this), delay);

    this.startInputTracking();
  }

  onLogout() {
    this.clear();
    this.stopInputTracking();
  }

  onUserInput() {
    this.lastUserInputTime = Date.now();
  }

  onPlay(event) { 
    this.logger.debug ('media event fired: ' + event.type);
      this.curPlaying++;
  } 

  onPlayStop(event) { 
    this.logger.debug ('media event fired: ' + event.type);
      if (--this.curPlaying<=0)
        this.onUserInput();
  } 

  startInputTracking() {
    this.logger.info('levity AT: started input tracking');
    if (this.isBeingTracked) {
      this.logger.debug('already being tracked, returning');
    }
    this.isBeingTracked = true;

    // subscribe to input events
    ACTIVITY_INPUT_EVENTS.forEach(event => window.addEventListener(event,
      this.onUserInput.bind(this)));
    this.onUserInput();
  }

  stopInputTracking() {
    this.logger.info('levity AT: stopped input tracking');
    this.isBeingTracked = false;
    ACTIVITY_INPUT_EVENTS.forEach(event => window.removeEventListener(event,
      this.onUserInput.bind(this)));
  }

  startMediaTracking() {
    this.logger.info('levity AT: started media tracking');

    // get all the video and audio elements on the page
    let mediaCollections = [];
    mediaCollections.push(document.getElementsByTagName('video'));
    mediaCollections.push(document.getElementsByTagName('audio'));

    this.logger.debug(`levity AT: tracking ${mediaCollections[0].length} videos`);
    this.logger.debug(`levity AT: tracking ${mediaCollections[1].length} audios`);

    mediaCollections.forEach (collection => {
      for (let mediaElem of collection) {
        ACTIVITY_PLAY_START_EVENTS.forEach(event => mediaElem.addEventListener(event, 
          this.onPlay.bind(this)));
        ACTIVITY_PLAY_STOP_EVENTS.forEach(event => mediaElem.addEventListener(event, 
          this.onPlayStop.bind(this)));
      }
    } );
  }

  showDialogIfNeeded() {
    let state = this.getState(true);
    this.logger.debug (
      `levity AT: showDialogIfNeeded: ${JSON.stringify(state)}`);
    if (!state.isActive && state.isVisible) {
      // check if we're close to the max ext session limit
      if (currentSession.createTimeInMS) {
        if ((currentSession.expireTimeInMS-currentSession.createTimeInMS) >= 
            (MAX_EXT_SESSION_DURATION_IN_MILLIS-15000) ) {
          this.logger.debug (`levity AT: dialog not shown, ext session maxed out`);
          return;
        }
      }
      // show dialog
      Swal.fire({
        title: 'Your session is about to expire!',
        text: 'Do you want to stay signed in?',
        icon: 'warning',
        allowOutsideClick: false,
        showConfirmButton: true,
        confirmButtonText: 'Yes, keep me signed in',
        confirmButtonColor: '#008000',
        showCancelButton: true,
        cancelButtonText: 'No, sign me out',
        cancelButtonColor: '#FF0000',
        timer: ACTIVITY_DIALOG_MAX_DURATION_IN_MILLIS,
        timerProgressBar: true
      })
      .then((result) => {
        if (result.value) {
          this.logger.debug('levity AT: showDialogIfNeeded:Got keep me logged in');
          this.onUserInput();
          logger.debug('calling forced session extension...');
          sessionExtensionCheckInFlight = true;
          retrieveAWSSession(getCookiesState(),true);
        }
        else { 
          if (result.dismiss) {
            this.logger.debug(
              `levity AT: showDialogIfNeeded:the dialog was dismissed by ${result.dismiss}`);
            // if the user tells us to sign her out immediately
            if (result.dismiss === Swal.DismissReason.cancel) {
              resetSession();
              deleteCBCookies();
              publishLogoutEvent();
              return;
            }
            // if the dialog was dismissed by timer:
            // let the session expire shortly in a natural way to avoid race condition
            // when the dialog is shown in multiple windows
            // i.e. when user clicked on "stay logged in" in one window when 1 sec left
            // and the call to catapult to extend the session has not returned yet
            if (result.dismiss === Swal.DismissReason.timer) {
              // since it's possible that there are multiple visible windows
              // so the inactivity dialog is shown in more than one window
              // of course the dialog is dismissed programatically (and we won't be here)
              // when levity discovers that the session has been extended in another window
              // but there's a possible race condition 
              // check whether the user hit "stay logged in" in a different tab
              // and the session has been extended if not clean up/log out
              // it's possible that there was another visible and active window
              // we need it to give it a chance to extend the session
              setTimeout ( () => {
                if ( !currentSession.isLoggedIn || 
                     (Date.now() > 
                     currentSession.expireTimeInMS-ACTIVITY_CHECK_BEFORE_EXP_IN_MILLIS*2) ) {
                  logger.warn ('inactivity dialog expired, logging off...');
                  resetSession();
                  deleteCBCookies();
                  publishLogoutEvent();
                  return;
                }
              }, 5000);
            }
          }
        }
      });

    }
    else {
      this.logger.debug (`levity AT: dialog not shown`);
    }
  }

  getState(isDialog=false) {
    let lookBackTimeInMillis = isDialog ? 
      ACTIVITY_DIALOG_LOOK_BACK_IN_MILLIS : ACTIVITY_LOOK_BACK_IN_MILLIS;
    let res = {
      isBeingTracked: this.isBeingTracked,
      isActive: false,
      isActiveReason: '',
      hasFocus: document.hasFocus(),
      isVisible: !document.hidden,
      isMediaPlaying: (this.curPlaying > 0) ? true : false,
      idleTimeSinceLastInputInSecs: undefined
    };

    if (this.isBeingTracked) {
      if (this.curPlaying > 0) {
        res.isActive = true;
        res.isActiveReason = 'media is playing';
      }
      else {
        let idleMillis = Date.now() - this.lastUserInputTime;
        res.idleTimeSinceLastInputInSecs = Math.floor(idleMillis/1000);
        if (res.isVisible) { 
          if (idleMillis < lookBackTimeInMillis) {
            res.isActive = true;
            res.isActiveReason = 
              `visible and user activity within last ${lookBackTimeInMillis/60000} mins`;
          }
        }
      }
    }
    return res;
  }
}

  // init
  //---------------------------------------------------------------------------
  function init(paramObject) {
    logger.info('cb.core.iam init()');

    if (utils.DeploymentProfile &&
       (utils.DeploymentProfile.showFatalAlerts===false))
      showFatalAlerts = false;

    // sanity check #1: the host page must be on https
    if (window.location.protocol!=='https:') {
      var error = 'Levity requires host page on HTTPS!!! ABORTING!!!'
      if (showFatalAlerts) alert(error);
      logger.fatal(error);
      throw error;
    }

    // sanity check #2: localhost won't work
    if ((window.location.hostname==='localhost') || 
        (window.location.hostname==='127.0.0.1')) {
      var error = 'Levity requires full domain, use /etc/hosts for local dev!!! ABORTING!!!'
      if (showFatalAlerts) alert(error);
      logger.fatal(error);
      throw error;
    }

    if (paramObject && paramObject.eventBus)
      eventBus = paramObject.eventBus;
    else
      eventBus = new EventEmitter(); // create our own one
    
    var apiKey = utils.getAPIKey();

    // get the bsauth partial url
    if (utils.DeploymentProfile && utils.DeploymentProfile.iam) {

      var iam = utils.DeploymentProfile.iam;

      if (iam.authServiceURI) {
        // replace the placeholder {{API-KEY}} with the actual apiKey
        bsauthPartialUrl = iam.authServiceURI;
      }

      if (iam.appId) {
        // support for multiple appIds for pros
        if (iam.appId instanceof Array) {
          cognitoPartialUrl += '&appId='+iam.appId.toString();
        }
        else { 
          bsauthPartialUrl += '&appId='+iam.appId;
          cognitoPartialUrl += '&appId='+iam.appId;
        }
      }

      if (iam.requiresLogin) {
        if (iam.loginURI) {
          requiresLogin = iam.requiresLogin;
          loginURI = iam.loginURI;
          // append with the current url if the configured login url ends with &DURL=
          // yes, I'm too lazy to support this parameter when it's not the last one ;-)
          if (endsWith(loginURI, '&DURL='))
            loginURI += encodeURIComponent(window.location.href);
          logger.info("requiresLogin is ON, loginURI=" + loginURI);
        }
        else {
          throw 'utils.DeploymentProfile.iam.requiresLogin is true,' +
                'but utils.DeploymentProfile.iam.loginURI not defined!';
        }
      }

      // new aws stuff for 4.*
      if (iam.aws) {

        isAWS = true;
        cbAWSDomains = iam.aws.cbAWSDomains;

        bsauthPartialUrl = 'catapult';

        if (iam.aws.catapultURI)
          cognitoPartialUrl = iam.aws.catapultURI;

        // make sure we retrieve temp creds for the catapult domain
        // unless no temp creds are needed
        if (cbAWSDomains.indexOf('none')===-1) {
          if ( cbAWSDomains.indexOf(AWS_COGNITO_CB_DOMAIN)===-1)
            cbAWSDomains.push(AWS_COGNITO_CB_DOMAIN);
        }
        else {
          areTempCredsNeeded = false;
        }
        
        cognitoPartialUrl += '&cbAWSDomains=' + cbAWSDomains.toString();

        var isFullRoleOrgInfo = iam.aws.cbFullRoleOrgInfo;
        if ( (isFullRoleOrgInfo===true) || (isFullRoleOrgInfo==='true') )
          cognitoPartialUrl += '&cbFullRoleOrgInfo=true';

        if (iam.aws.forceRefresh && 
          ((iam.aws.forceRefresh===true) || (iam.aws.forceRefresh==='true')) )
          forceAWSRefresh = true;
      }

      if (bsauthPartialUrl.startsWith('catapult')) {
        if (isAWS)
          isCatapultAuthOnly = true;
        else {
          let error = 
           'you must have aws enabled in levity profile for catapult authn';
          if (showFatalAlerts) alert(error);
          logger.fatal(error);
          throw error;
        }
      }
        
      // new partner/csr support for 5.*
      if (iam.loginToken) {
        if (iam.loginToken.loginCookie) {
          if (iam.loginToken.loginCookie.name){
            USER_COOKIE_NAME.CBLOGIN = iam.loginToken.loginCookie.name;
            logger.info("session cookie set to:" + USER_COOKIE_NAME.CBLOGIN);
          }
          if (iam.loginToken.loginCookie.domain){
            cookiesDomain = iam.loginToken.loginCookie.domain;
            logger.info("session cookie domain set to:" + cookiesDomain);
            // for partners set default session length to 1 hour
            if (!endsWith(cookiesDomain, 'collegeboard.org')) {
              isPartner = true;
              sessionDurationInSecs = 3600;
              logger.info("default session max length in secs set to:" + sessionDurationInSecs);
              // set correct values in the deployment profile
              iam.loginToken.type = 'Bearer';
              iam.sessionCheck = false;
            }
          }
        }
        if (iam.loginToken.type){
          loginTokenType = iam.loginToken.type;
          if (loginTokenType==='CBLoginWeak') {
            isLoginRaottBased = true;
            forceAWSRefresh = true; // for now, TODO may reconsider
          }
          logger.info("loginTokenType set to:" + loginTokenType);
        }

        if (iam.loginToken.env){
          loginTokenEnv = iam.loginToken.env;
          logger.info("loginTokenEnv set to:" + loginTokenEnv);
        }
      }

      if ( (iam.sessionCheck!==undefined) && (iam.sessionCheck===false) ) {
        sessionCheck = false;
        if (isPartner && isAWS) {
          logger.info("session check is set to aws for partners");
        }
        else {
          logger.info("session check is set to local!");
          logger.info("data protection will be disabled!");
        }
        if (utils.DeploymentProfile.dataProtector) {
          utils.DeploymentProfile.dataProtector.protectionEnabled = false;
        }
      }

      if (iam.treeEnvCookieName) {
        USER_COOKIE_NAME.LOWER_ENV = iam.treeEnvCookieName;
        logger.info("cb tree env cookie set to:" + USER_COOKIE_NAME.LOWER_ENV);
      }

      if (iam.sessionCheckIntervalInSeconds) {
        INTERVAL_IN_MILLIS = iam.sessionCheckIntervalInSeconds*1000;
        logger.info("session check interval in millis set to:" + INTERVAL_IN_MILLIS);
      }

      if (iam.maxLoginSessionLengthInMinutes){
        sessionDurationInSecs = parseInt(iam.maxLoginSessionLengthInMinutes)*60;
        logger.info("session max length in secs set to:" + sessionDurationInSecs);
      }
    }

    // create activity tracker singleton, enable it only for stus and pros
    // disable for partners 
    // requires catapult
    if ((loginTokenType==='CBLogin') && !isPartner &&
        (USER_COOKIE_NAME.CBLOGIN==='cb_login') ) {
      if (isAWS)
        activityTracker = new ActivityTracker(eventBus,logger);
      else 
        logger.info ('Session extension initiation is disabled, requires catapult');
    }
    else {
      logger.info ('Session extension is not supported for this type of users');
    }

    // adjust loglevel and other stuff based on the current env (prod vs nonprod)
    // or on the supplied config in the deployment profile
    adjustWithEnv();

    // loadSession (from the browser session storage)
    loadSessionFromBrowserSS();

    // do the placeholder substitution
    bsauthPartialUrl = bsauthPartialUrl.replace(/{{API-KEY}}/g, apiKey);

    //
    logger.debug('levity iam options in effect: ' + '\nbsauthPartialUrl : ' + bsauthPartialUrl +
      '\ncatapultPartialUrl : ' + cognitoPartialUrl +
      '\nsessionCheck : ' + sessionCheck + '\nrequiresLogin : ' + requiresLogin +
      (requiresLogin? '\nloginURI : ' + loginURI : '') + '\n');
    //

    // set up the timer to check the cookies
    intervalID = setInterval(onTimer,INTERVAL_IN_MILLIS);

    // check session right away
    var cookiesState = getCookiesState();

    // sessionId is stored as a hash in the sessionStorage
    if ( cookiesState.sessionId &&
         hashCode(cookiesState.sessionId) === currentSession.sessionId) {
      currentSession.sessionId = cookiesState.sessionId;
    }
    onTimer();
  }

  // onTimer
  //---------------------------------------------------------------------------
  function onTimer() {

    // new event handling
    checkSession();
  }

  // checkSession
  //---------------------------------------------------------------------------
  function checkSession() {

    var cookiesState = getCookiesState(); // based on cookies only
    let curTime = Date.now();

    // if user was not logged in before and is not logged in now
    // - do nothing unless login is mandatory for the page
    if ( !currentSession.isLoggedIn && !cookiesState.isLoggedIn ) {
      redirectToLoginIfRequired();
      return;
    }

    // if the user was logged in before AND is logged in now
    // AND the sessionId (cb_login cookie value) HAS NOT CHANGED
    // AND session has not expired
    // - do nothing (she is still in the same session)
    if ( currentSession.isLoggedIn && cookiesState.isLoggedIn &&
         (currentSession.sessionId === cookiesState.sessionId) &&
         (curTime < currentSession.expireTimeInMS) ) {

      // check if the aws temp creds don't exist or have expired
      if (!areTempCredsNeeded) {
        /* check the expiration of the catapult authz token
        if (!tempAWSCreds.parsedJwtToken || 
           ( 
             ((curTime+INTERVAL_IN_MILLIS*5) <= currentSession.expireTimeInMS )
             &&
             ((curTime+INTERVAL_IN_MILLIS*5) >= tempAWSCreds.parsedJwtToken.exp*1000 )
           ) ){
          retrieveAWSSession(cookiesState);    
        }
        */
        return;
      }

      if (isAWS && (cbAWSDomains.length > 0)) {

        // check if session should be extended
        // currently supported for students and pros only
        if ( !sessionExtensionCheckInFlight &&
          (currentSession.basicProfile.isStudent || currentSession.basicProfile.isProfessional)
          && activityTracker &&
          ((curTime+ACTIVITY_CHECK_BEFORE_EXP_IN_MILLIS)>=currentSession.expireTimeInMS) ) {
          let activityState = activityTracker.getState();
          if (activityState.isActive) {
            // initiate session extension call
            logger.debug('calling session extension...');
            sessionExtensionCheckInFlight = true;
            retrieveAWSSession(cookiesState,true);
            return;
          }
        }

        // check if tokens need to be refreshed
        if ( !tempAWSCreds[cbAWSDomains[0]]
             || ( (curTime+INTERVAL_IN_MILLIS*5) >
            tempAWSCreds[cbAWSDomains[0]].creds.expireTime.getTime()) ) {

          //logger.debug('checkSession #1');
          retrieveAWSSession(cookiesState);
        }
      }

      return;
    }

    // if the user was logged in before AND is logged in now
    // AND the sessionId (cb_login cookie value) HAS NOT CHANGED
    // AND session HAS expired
    // - set not_logged_in
    // - publish Logout event
    if ( currentSession.isLoggedIn && cookiesState.isLoggedIn &&
         (currentSession.sessionId === cookiesState.sessionId) &&
         (Date.now() >= currentSession.expireTimeInMS) ) {
      logger.debug('checkSession #2');
      resetSession();
      deleteCBCookies();
      publishLogoutEvent();
      redirectToLoginIfRequired();
      return;
    }

    // if the user was logged in before but it's not logged in now
    // - clear the state/session
    // - set not_logged_in
    // - publish Logout event
    if ( currentSession.isLoggedIn && !cookiesState.isLoggedIn ) {
      logger.debug('checkSession #3');
      resetSession();
      publishLogoutEvent();
      redirectToLoginIfRequired();
      return;
    }

    // if the user was logged in before and is logged in now
    // AND the sessionId (cb_login cookie value) HAS CHANGED
    // Either logout and then login has happened or the same user was asked
    // to log in (enter password) by some CB webapp
    // CONSIDER: handling the same user login verification
    //    without canceling/creating user session by checking if the username
    //    did not change (the username for the new session must be retrieved by
    //    async ajax call with <1 sec timeout otherwise we may show the new user
    //    the previous user's info): not sure if it's worth the effort
    // - clear the state/session
    // - retrieve the new session from the network
    // - set logged_in
    // - publish Logout event
    if ( currentSession.isLoggedIn && cookiesState.isLoggedIn &&
         (currentSession.sessionId !== cookiesState.sessionId) ) {
      logger.debug('checkSession #4, session extension check');
      // first reset session and send the logout event
      //resetSession();
      //publishLogoutEvent();

      // check if it's the same user with a new session (extended or pw confirmation)
      sessionExtensionCheckInFlight = true;
      retrieveSession(cookiesState);
      return;
    }

    // if the user was not logged in before but is logged in now (login has happened)
    // - retrieve the new session from the network
    // - set logged_in, sessionId and expireTimeInMS
    // - publish Login event
    if ( !currentSession.isLoggedIn && cookiesState.isLoggedIn ) {
      logger.debug('checkSession #5');

      // for partners since we can't check if the secure flag is set 
      // just rewrite the login cookie with the same value with the secure flag set
      if (isPartner){
        logger.debug('rewriting the login cookie with the secure flag');
        cookie.set(USER_COOKIE_NAME.CBLOGIN, cookie.get(USER_COOKIE_NAME.CBLOGIN),
           {path: '/',domain: cookiesDomain,secure:true} );
      }

      // make an ajax call to validate and retrieve the session from the backend
      retrieveSession(cookiesState);
      return;
    }

  }

  // getCookiesState
  //---------------------------------------------------------------------------
  function getCookiesState() {

    var state = {
        isLoggedIn: false,
        firstName: '',
        isRegUser: false,
        isProUser: false
    },
    studentCookie        = cookie.get(USER_COOKIE_NAME.STUDENT_NAME),
    proCookie            = cookie.get(USER_COOKIE_NAME.PRO_NAME),
    studentCacheIdCookie = cookie.get(USER_COOKIE_NAME.STUDENT_CACHE_SESSION_ID),
    proCacheIdCookie     = cookie.get(USER_COOKIE_NAME.PRO_CACHE_SESSION_ID),
    cbLoginCookie        = cookie.get(USER_COOKIE_NAME.CBLOGIN);

    if (isLoginRaottBased) {
      if (raott) { 
        state.isLoggedIn = true;
        state.sessionId = raott;
        state.cacheId = raott;
      }
      return state;
    }

    if (proCookie === 'invalid_user') {
      proCookie = null;
    }

    if (studentCookie === 'invalid_user') {
      studentCookie = null;
    }

    state.isRegUser = !!studentCookie; // get equivalent boolean
    state.isProUser = !!proCookie;
    state.firstName = (state.isRegUser ? studentCookie : (state.isProUser ? proCookie : "")).toLowerCase();
    state.isLoggedIn = !!cbLoginCookie;
    state.sessionId = cbLoginCookie;
    state.cacheId = state.isRegUser ? studentCacheIdCookie 
      : state.isProUser ? proCacheIdCookie : cbLoginCookie;

    //logger.debug("Got cookies state: " + JSON.stringify(state));

    return state;
  }

  // loginWithRAOTT
  //---------------------------------------------------------------------------
  function loginWithRAOTT(paramRaott,verifierName=null, verifierValue=null) {

    if (!paramRaott) {
      logger.error ('loginWithRAOTT requires a raott');
      return;
    }

    if (verifierName && !verifierValue) {
      logger.error 
        ('loginWithRAOTT requires verifierValue if verifierName is supplied');
      return;
    }

    // reset the current number of logins
    nOfAWSAttempts = 0;

    raott = paramRaott;
    if (verifierName)
      raottVerifierString = ` ${verifierName} ${verifierValue}`;

    retrieveAWSSession(getCookiesState());
  }

  // logoutWithRAOTT
  //---------------------------------------------------------------------------
  function logoutWithRAOTT() {

    resetSession();
    publishLogoutEvent();
  }

  // getSession
  //---------------------------------------------------------------------------
  function getSession() {

    return currentSession;
  }

  // getEventBus
  //---------------------------------------------------------------------------
  function getEventBus() {

    return eventBus;
  }


  // resetSession
  //---------------------------------------------------------------------------
  function resetSession() {

    // reset state
    currentSession = {};
    currentSession.isLoggedIn = false;
    currentSession.sessionId = "not_logged_in";

    // reset raott
    raott = null;

    // reset legacy state
    currentLegacyState = {};
    currentLegacyState.isLoggedIn = false;

    // clear AWS stuff in memory
    tempAWSCreds = {
      cognitoIdentityPoolId : null,
      subject : null
    };

    if (window.AWS && window.AWS.config)
      AWS.config.credentials = null;

    // clear the browser session storage
    sessionStorage.removeItem(SESSION_STORAGE_IAM_KEY);

    // it's OK to call removeItem on the non-existing entries
    cbAWSDomains.forEach(function(domain) {
      sessionStorage.removeItem(AWS_CREDS_KEY_PREFIX+domain);
    });
    sessionStorage.removeItem(AWS_CREDS_SUBJECT_KEY_PREFIX);
    sessionStorage.removeItem(AWS_CREDS_POOL_KEY_PREFIX);

    //publishLogoutEvent();
  }

  // deleteCBCookies
  //---------------------------------------------------------------------------
  function deleteCBCookies() {
    logger.warn('levity: session is invalid: deleting login cookie');
    cookie.remove(USER_COOKIE_NAME.CBLOGIN, {path: "/", domain: cookiesDomain});
    cookie.remove(USER_COOKIE_NAME.LOWER_ENV, {path: "/", domain: cookiesDomain});
  }

  // redirectToLoginIfRequired
  //---------------------------------------------------------------------------
  function redirectToLoginIfRequired() {

    // only if this page config requested mandatory login
    if (requiresLogin) {
      logger.info('requiresLogin=true, user is not logged in, redirecting...');
      // redirect to login
      window.location.assign(loginURI);
    }
  }

  // store iam session in a browser session storage
  // ----------------------------------------------------------------------------
  function storeSessionInBrowserSS() {

    if (isPartner)
      return;

    // store sessionId as a hash for addl security
    var sessionId = currentSession.sessionId;
    currentSession.sessionId = hashCode(sessionId);
    sessionStorage.setItem(SESSION_STORAGE_IAM_KEY, JSON.stringify(currentSession));
    currentSession.sessionId = sessionId;
  }

  // rerieve iam session in a browser session storage
  // ----------------------------------------------------------------------------
  function loadSessionFromBrowserSS() {

    if (isPartner)
      return;

    currentSession = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_IAM_KEY)); // null is fine

    if (currentSession!==null) {

      // set the legacy session correctly
      currentLegacyState.isLoggedIn = currentSession.isLoggedIn;
      currentLegacyState.isRegUser = currentSession.basicProfile.isStudent;
      currentLegacyState.isProUser = currentSession.basicProfile.isProfessional;
      currentLegacyState.firstName = currentSession.basicProfile.firstName;
    }
    else
      resetSession();
  }


    function getHashId(){
        return currentSession.hashId
    }

  // ----------------------------------------------------------------------------
  function getAuthenticationToken() {
  // ----------------------------------------------------------------------------
    
    return loginTokenType + ' ' + currentSession.sessionId;
  }

  // ----------------------------------------------------------------------------
  function getTempAWSCreds(cbAWSDomain) {
  // ----------------------------------------------------------------------------

    return tempAWSCreds[cbAWSDomain] ? tempAWSCreds[cbAWSDomain].creds : null;
  }

  // ----------------------------------------------------------------------------
  function getTempAWSCredsSubject() {
  // ----------------------------------------------------------------------------

    return tempAWSCreds.subject;
  }

  // ----------------------------------------------------------------------------
  function getJWTToken() {
  // ----------------------------------------------------------------------------

    return tempAWSCreds.jwtToken;
  }

  // ----------------------------------------------------------------------------
  function setJWTToken(token) {
  // ----------------------------------------------------------------------------

    tempAWSCreds.jwtToken = token;
    tempAWSCreds.parsedJwtToken = jwt_decode(token)
  }

  // set temp AWS credentials
  // ----------------------------------------------------------------------------
  function setTempAWSCreds(creds,subject,poolId,cbAWSDomain) {
  // ----------------------------------------------------------------------------
    // first store it in memory
    tempAWSCreds[cbAWSDomain] = {};
    tempAWSCreds[cbAWSDomain].creds = creds;
    tempAWSCreds.subject = subject;
    tempAWSCreds.cognitoIdentityPoolId = poolId;
  }

  // adjustWithEnv()
  // dynamically adjusts things based on whether it's in prod or nonprod
  // returns current env (oak,palm and etc for nonprod and pine for prod)
  //---------------------------------------------------------------------------
  function adjustWithEnv() {

    // env in the cookie takes pecedence over the one specified in config
    var env = cookie.get(USER_COOKIE_NAME.LOWER_ENV);

    if (!env && loginTokenEnv)
      env = loginTokenEnv;

    if (!env || (env==='pine') || (env==='prod')) {
      // we're in prod
      if (!env)
        env = 'pine';
      rootLogger.setLevel(utils.Logger.Level.INFO);
    }
    else {
      // we're in lower env, enable debug logging
      rootLogger.setLevel(utils.Logger.Level.DEBUG);
    }

    return env;
  }

  // finalizeMyStyleURLWithEnv()
  // replaces {{TREE-ENV}} with the current lower env if it exists
  // replaces {{PROD-NONPROD}} with 'prod' or 'nonprod'
  //---------------------------------------------------------------------------
  function finalizeMyStyleURLWithEnv(srcUrlOrUrls) {

    var env = adjustWithEnv();
    var srcUrl = srcUrlOrUrls;

    if (srcUrlOrUrls instanceof Object ) {
      if ((env==='pine') || (env==='prod'))
        srcUrl = srcUrlOrUrls.prod;
      else
        srcUrl = srcUrlOrUrls.nonprod;
    }

    var res = srcUrl;
    res = srcUrl.replace(/{{TREE-ENV}}/g, env);

    var prodNonprod = ((env==='pine') || (env==='prod')) ? 'prod' : 'nonprod';
    res = res.replace(/{{PROD-NONPROD}}/g, prodNonprod);

    return res;
  }

  // retrieveSession over the network
  //---------------------------------------------------------------------------
  function retrieveSession(cookiesState) {

    // retrieve the AWS session if needed
    if (isAWS)
      retrieveAWSSession(cookiesState);

    if (isCatapultAuthOnly) 
      return;

    if (!sessionCheck) {
      if (sessionExtensionCheckInFlight) {
        console.error ('session extension is not supported for this config');
        resetSession();
        publishLogoutEvent();
        sessionExtensionCheckInFlight = false;
        return;
      }

      // fake the call and supply data from the cookies only
      var namespace = cookiesState.isRegUser ? 'ST' : 
        cookiesState.isProUser ? 'PF' : '';
      var data = {
        "authStatus":"Success",
        "sessionInfo": {
          "identityKey": {
            "namespace": namespace,
            "userName":cookiesState.firstName
          },
          "unifiedSessionId":cookiesState.sessionId,
          "expireInSeconds": sessionDurationInSecs,
          "csrId":null
        },
        "basicProfile": {
          "firstName":cookiesState.firstName,
          "lastName" :"",
          "zip5":"",
          "emailAddress":""
        }
      };

      // make the fake call using the timer so we relinquish control properly
      setTimeout(onSuccessfulSessionRetrievalCB, 0, data);

      return;
    }

    var sessionId = cookiesState.sessionId;

    // else continue with a real sessions check over the network
    nOfSessionChecks++;

    var bsauthUrl = finalizeMyStyleURLWithEnv(bsauthPartialUrl);

    if (nOfSessionChecks > MAX_N_OF_SESSION_CHECKS) {
      logger.error ('max number of session check attempts failed: ' + bsauthUrl);
      if (intervalID!==INVALID_INTERVAL_ID) {
        clearInterval(intervalID);
        intervalID = INVALID_INTERVAL_ID;
      }
      logger.debug('publishing LoginFailed event');
      eventBus.trigger(events.LoginFailed);
      return;
    }

    logger.debug ('calling auth service:' + bsauthUrl + " current nOfSessionChecks=" + nOfSessionChecks +
        ' isAWSTempCredsRefreshInFlight=' + isAWSTempCredsRefreshInFlight);

    axios.get(bsauthUrl, {
          timeout: INTERVAL_IN_MILLIS,
          headers: {
            Authorization: `${loginTokenType} ${sessionId}`
          }
    })
    .then(r => r.data)
    .then(onSuccessfulSessionRetrievalCB)
    .catch(onFailedSessionRetrievalCB);
  }

  // onSuccessfulSessionRetrievalCB
  //---------------------------------------------------------------------------
function onSuccessfulSessionRetrievalCB(data) {

  try {

    logger.debug('onSuccessfulSessionRetrievalCB(): ' + JSON.stringify(data));

    // example of data received on success:
    // {"authStatus":"Success","sessionInfo":{"sessionId":null,
    // "identityKey":{"namespace":"st","personId":"83752131","accountId":"50389875","userName":"LEVPROD"},
    // "csrId":null,"scopedAppIdList":null,"createdDate":1456520011000,"oamSessionId":null,"userDn":null,
    // "unifiedSessionId":"94C8B8B5-83C5-9297-E52D-E7C52F4EEC68","tst":null,"expireInSeconds":5990,"sharedData":null},
    // "authnLevel":"2","basicProfile":{"firstName":"Levprod","lastName":null,"zip5":null,"zip4":null,
    // "emailAddress":null,"dataOrigin":null,"status":null}};

    // example of data received on error: (the code is still 200, but session is not found)
    // {"authStatus":"Fail","sessionInfo":null,"authnLevel":null,"basicProfile":null}


    // TODO if success
    // on successful return in the callback:
    // - set logged_in, sessionId and expireTimeInMS
    // - publish Login event
    // eventBus.trigger(events.Login);
    if (data.authStatus && (data.authStatus.toUpperCase() === 'SUCCESS')) {

      var publishLoginEventFlag = true;
      let publishLoginExtensionEventFlag = false;

      if (isAWS && currentSession.isLoggedIn && !sessionExtensionCheckInFlight) {
        logger.debug(
          'onSuccessfulSessionRetrievalCB(): was already called from AWS, returning');
        return;
      }

      if (sessionExtensionCheckInFlight) {
        publishLoginEventFlag = false;
        publishLoginExtensionEventFlag = true;
      }

      nOfSessionChecks = 0;
      currentSession.hashId = data?.sessionInfo?.identityKey?.hashId
      currentSession.isLoggedIn = true;
      currentSession.sessionId = data.sessionInfo.unifiedSessionId;
      if (data.sessionInfo.createdDate)
        currentSession.createTimeInMS = new Date(data.sessionInfo.createdDate).getTime();
      currentSession.loginTokenType = loginTokenType;
      currentSession.csrId = data.sessionInfo.csrId;
      if (!isPartner || (data.sessionInfo.expireInSeconds < sessionDurationInSecs)) {
        sessionDurationInSecs = data.sessionInfo.expireInSeconds;
      }
      currentSession.expireTimeInMS = Date.now() + sessionDurationInSecs * 1000;
      currentSession.basicProfile = data.basicProfile ? data.basicProfile : {};
      currentSession.basicProfile.emailAddress = '';
      currentSession.basicProfile.userName = data.sessionInfo.identityKey.userName;
      currentSession.basicProfile.namespace = data.sessionInfo.identityKey.namespace;
      currentSession.basicProfile.hashedId =
        data.sessionInfo.identityKey.personId && data.sessionInfo.identityKey.accountId ?
          (Number(data.sessionInfo.identityKey.personId) +
            Number(data.sessionInfo.identityKey.accountId)).toString() : 'invalid';
      currentSession.basicProfile.isStudent =
        currentSession.basicProfile.namespace.toUpperCase() === 'ST' ? true : false;
      currentSession.basicProfile.isProfessional =
        currentSession.basicProfile.namespace.toUpperCase() === 'PF' ? true : false;
      currentSession.basicProfile.isCSR =
        currentSession.basicProfile.namespace.toUpperCase() === 'CR' ? true : false;

      if (data.basicProfile) {
        currentSession.basicProfile.firstName = data.basicProfile.firstName;
      }

      // check if it was a possible session extension
      if (sessionExtensionCheckInFlight) {
        sessionExtensionCheckInFlight = false;
        if (currentSession.isLoggedIn && currentSession.basicProfile 
            && currentSession.basicProfile.userName) {
          if (currentSession.basicProfile.userName === 
              data.sessionInfo.identityKey.userName) {
            // session was extended
            logger.info ('login session has been extended');
            logger.debug('publishing LoginExtended event');
            eventBus.trigger(events.LoginExtended);
          }
          else {
            resetSession();
            publishLogoutEvent();
          }
        }
      }

      // legacy stuff
      currentLegacyState.isLoggedIn = currentSession.isLoggedIn;
      currentLegacyState.isRegUser = currentSession.basicProfile.isStudent;
      currentLegacyState.isProUser = currentSession.basicProfile.isProfessional;
      currentLegacyState.firstName = currentSession.basicProfile.firstName;

      // store in the browser session storage
      storeSessionInBrowserSS();

      // publish the login event
      if (publishLoginEventFlag)
        publishLoginEvent();
    }
    else {
      // session is no longer valid: delete the session cookie
      deleteCBCookies();
      onFailedSessionRetrievalCB('CB session has expired');
    }
  }
  catch (err) {
    logger.error(err.message);
  }
}

  // onFailedSessionRetrievalCB
  //---------------------------------------------------------------------------
  function onFailedSessionRetrievalCB(data) {
    logger.error('Call to cb session check failed: ' + JSON.stringify(data));

    if (sessionExtensionCheckInFlight) {
      sessionExtensionCheckInFlight = false;
      publishLogoutEvent();
    }

    // SSO session is invalid
    resetSession();
    // remove the cb login cookie (session has expired)
    // commented out since it's too aggressive to log a person out
    // if one call to BSAuth failed
    //deleteCBCookies();

    redirectToLoginIfRequired();
  }

  // retrieveAWSSession over the network
  //---------------------------------------------------------------------------
  function retrieveAWSSession(cookiesState, sessionExt=false) {

    /*
    if (!sessionCheck) {
      logger.warn('session check is set to local: will not retrieve AWS session!');
      return;
    }
    */

    if (isAWSTempCredsRefreshInFlight) {
      logger.debug('retrieve AWS session is already inflight, returning');
      return;
    }

    if (nOfAWSAttempts >= MAX_N_OF_SESSION_CHECKS) {
      if (nOfAWSAttempts++ === MAX_N_OF_SESSION_CHECKS) {
        isAWSTempCredsRefreshInFlight = false;
        logger.error ('max number of AWS session check attempts failed: ' +
          cognitoPartialUrl);
        if (!isLoginRaottBased) {
          publishAWSLoginFailedEvent();
        }

        // cancel the timer if it is the initial session check
        if (isAWS && isCatapultAuthOnly && !getTempAWSCredsSubject()) {
          if (intervalID!==INVALID_INTERVAL_ID) {
            clearInterval(intervalID);
            intervalID = INVALID_INTERVAL_ID;
          }
        }
      }
      return;
    }

    isAWSTempCredsRefreshInFlight = true;
    nOfAWSAttempts++;

    var sessionId = cookiesState.sessionId;
    if (isLoginRaottBased && raottVerifierString) {
      sessionId += raottVerifierString;
    }
    var cacheNonce = 
      (forceAWSRefresh) ?
      //(forceAWSRefresh || !currentSession.isLoggedIn || sessionExtensionCheckInFlight) ? 
      Date.now() : hashCode(cookiesState.cacheId);

    var cognitoUrl = finalizeMyStyleURLWithEnv(cognitoPartialUrl) +
        '&cacheNonce=' + cacheNonce;

    // publish AWSLoginStarted event after the rest of the JS on the page
    // has been loaded
    // Do NOT publish this event for the temp creds refresh
    if (!getTempAWSCredsSubject()) {
      executeAsEarlyAsPossible ( function() {
        if (isAWSTempCredsRefreshInFlight) {
          logger.debug('publishing AWSLoginStarted event');
          eventBus.trigger(events.AWSLoginStarted);
        }
      });
    }

    logger.debug ('calling AWS auth service:' + cognitoUrl +
      ' sessionId=' + sessionId +
      ' nOfAWSAttempts=' + nOfAWSAttempts +
      ' isAWSTempCredsRefreshInFlight=' + isAWSTempCredsRefreshInFlight);

    let localLoginTokenType = loginTokenType;

    if (sessionExt) {
      localLoginTokenType += 'X';
    }

    axios.get(cognitoUrl, {
          timeout: INTERVAL_IN_MILLIS*2,
          headers: {
            Authorization: `${localLoginTokenType} ${sessionId}`
          }
    })
    .then(onSuccessfulAWSSessionRetrievalCB)
    .catch(onFailedAWSSessionRetrievalCB);
  }

  // onSuccessfulAWSSessionRetrievalCB
  //---------------------------------------------------------------------------
function onSuccessfulAWSSessionRetrievalCB(response) {
  try {
    var data = response.data;

    isAWSTempCredsRefreshInFlight = false;

    //logger.debug('onSuccessfulAWSSessionRetrievalCB(): ' + JSON.stringify(data));
    logger.debug('onSuccessfulAWSSessionRetrievalCB()');
    // check if the local time is out of whack
    var dateHeader = response.headers['date'];
    if (dateHeader && (dateHeader.length > 10)) {
      logger.debug('serverTimeStamp:' + dateHeader);
      // check the difference
      var serverTime = new Date(dateHeader).getTime();
      diffWithLocalInSecs = (Date.now() - serverTime) / 1000;
      logger.debug('local time difference with server in seconds: ' + diffWithLocalInSecs);
      if (Math.abs(diffWithLocalInSecs) > INVALID_LOCAL_CLOCK_DIFF_IN_SECS) {
        executeAsEarlyAsPossible(function () {
          logger.error('publishing InvalidLocalClock event, time diff in secs:'
            + diffWithLocalInSecs);
          eventBus.trigger(events.InvalidLocalClock);
        });
      }
    }
    else {
      logger.warn('could not get date response header');
    }

    var isRefresh = getTempAWSCredsSubject() === null ? false : true;

    // publish AWSLoginFinished event
    // Do NOT publish this event for the temp creds refresh
    if (!isRefresh) {
      logger.debug('publishing AWSLoginFinished event');
      eventBus.trigger(events.AWSLoginFinished);
    }

    if (data && data.cbUserProfile &&
      data.cbUserProfile.authStatus &&
      (data.cbUserProfile.authStatus.toUpperCase() === 'SUCCESS')) {

      //if ( currentSession.sessionId && 
      //    (currentSession.sessionId!== data.sessionInfo.unifiedSessionId)

      // store user AWS creds
      for (var domain in data) {
        var acctData = data[domain];
        if (acctData.Credentials && acctData.Credentials.AccessKeyId) {
          var creds = acctData.Credentials;

          var awsCredentials = {
                accessKeyId: creds.AccessKeyId,
                secretAccessKey: creds.SecretAccessKey,
                sessionToken: creds.SessionToken,
                expireTime: new Date(creds.Expiration)
          }

        //   var awsCredentials = new AWS.Credentials(
        //     creds.AccessKeyId, creds.SecretAccessKey, creds.SessionToken);
        //   awsCredentials.expireTime = new Date(creds.Expiration);


          // additional guard against stale cache entry
          if (Date.now() >= awsCredentials.expireTime) {
            logger.warn('got expired AWS creds, likely because your computer clock is wrong!');
            onFailedAWSSessionRetrievalCB('CB session has expired');
            return;
          }

          var subject = acctData.SubjectFromWebIdentityToken;
          setTempAWSCreds(awsCredentials, subject, acctData.Audience, domain);
        }
        else {
          if (acctData.SubjectFromWebIdentityToken)
            tempAWSCreds.subject = acctData.SubjectFromWebIdentityToken;
        }
      }

      nOfAWSAttempts = 0;

      if (data.cbJwtToken) {
        setJWTToken(data.cbJwtToken);
      }

      // it's possible that the user loads the page that needs AWS hookup
      // into the tab that had a page which did not have it
      // so the user is logged in but has now AWS info which is needed to be retrieved
      // and AWSLogin event needs to published, but not the Login one
      if (!currentSession.isLoggedIn ||
        (!sessionCheck && currentSession.basicProfile.namespace === '') ||
         sessionExtensionCheckInFlight
        ) {
        if (sessionExtensionCheckInFlight) {
          let newSessionId = data.cbUserProfile.sessionInfo.unifiedSessionId;
          if (newSessionId !== cookie.get(USER_COOKIE_NAME.CBLOGIN)) {
            logger.debug(`setting ${USER_COOKIE_NAME.CBLOGIN} to ${newSessionId}`);
            cookie.set(USER_COOKIE_NAME.CBLOGIN, 
              newSessionId,
              {
                path: "/",
                domain: ".collegeboard.org",
                secure  : true
              });
          }
        }

        // since it may be coming from cache we need to adjust the number
        // of seconds left in the session
        if (data.cbJwtToken) {
          let createTime = jwt_decode(data.cbJwtToken).iat
          let secsSinceCreation = Math.floor(Date.now()/1000) - createTime;
          let expireInSecs = data.cbUserProfile.sessionInfo.expireInSeconds;
          let adjExpireInSecs =  expireInSecs - secsSinceCreation;
          logger.debug (`orig expInSecs: ${expireInSecs} adj:${adjExpireInSecs}`);
          data.cbUserProfile.sessionInfo.expireInSeconds = adjExpireInSecs;
        }

        onSuccessfulSessionRetrievalCB(data.cbUserProfile);
      }

      // publish AWSLogin event unless it's a temp creds refresh
      if (!isRefresh) {
        executeAsEarlyAsPossible(function () {
          logger.debug('publishing AWSLogin event');
          eventBus.trigger(events.AWSLogin);
        });
      }
      else {
        logger.debug('publishing AWSLoginRefresh event');
          eventBus.trigger(events.AWSLoginRefresh);
      }
    }

    else {
      onFailedAWSSessionRetrievalCB(data);
    }
  }
  catch (err) {
    logger.error(err.message);
  }
}

  // onFailedAWSSessionRetrievalCB
  //---------------------------------------------------------------------------
  function onFailedAWSSessionRetrievalCB(data) {

    if ( data.response && data.response.data)
      data = data.response.data;

    isAWSTempCredsRefreshInFlight = false;
    logger.error('Call to catapult failed: ' + JSON.stringify(data));
    lastErrorMsg = data.errorMessage ? 
      data.errorMessage : 'network/proxy error';

    // publish AWSLoginFinished event unless it's a temp creds refresh
    if (!getTempAWSCredsSubject()) {
      logger.debug('publishing AWSLoginFinished event');
      eventBus.trigger(events.AWSLoginFinished);

      if (isLoginRaottBased) {
        nOfAWSAttempts = MAX_N_OF_SESSION_CHECKS;
        publishAWSLoginFailedEvent();
      }
    }

    if (sessionExtensionCheckInFlight) {
      sessionExtensionCheckInFlight = false;
      //publishLogoutEvent();
    }

    // if it's a patrner and we get 400 that means the token has expired
    if (isPartner && (lastErrorMsg==='status code=400')){
      resetSession();
      deleteCBCookies();
      publishLogoutEvent();
    }
  }

  // publishLoginEvent
  //---------------------------------------------------------------------------
  function publishLoginEvent() {
    executeAsEarlyAsPossible ( function() {
      logger.debug('publishing Login event');
      eventBus.trigger(events.Login);

      // trigger legacy event
      eventBus.trigger(
        events.LegacyLoginLogout,
        currentLegacyState
      );
    });
  }

  // publishAWSLoginFailedEvent
  //---------------------------------------------------------------------------
  function publishAWSLoginFailedEvent() {

    let parsedError = 'unknown error';
    if (lastErrorMsg) { 
      try {
        parsedError = JSON.parse(lastErrorMsg);
      } 
      catch (error) {
        parsedError = lastErrorMsg;
      }
    }

    let errObj = parsedError instanceof Object ? parsedError : 
            {code: errorCodes.Generic, message: lastErrorMsg, retryable: false};

    logger.debug('publishing AWSLoginFailed event:'+ JSON.stringify(errObj));
    eventBus.emit(events.AWSLoginFailed, errObj);
  }

  // publishLogoutEvent
  //---------------------------------------------------------------------------
  function publishLogoutEvent() {

    executeAsEarlyAsPossible ( function() {
      logger.debug('publishing Logout event');
      eventBus.trigger(events.Logout);

      // trigger legacy event
      eventBus.trigger(
        events.LegacyLoginLogout,
        currentLegacyState
      );
    });
  }

  // openOrCreateCognitoDataset
  //---------------------------------------------------------------------------
  function openOrCreateCognitoDataset(callback) {

    // set AWS.config.credentials
    // since AWS CognitoSyncManager requires it
    AWS.config.credentials = getTempAWSCreds(AWS_COGNITO_CB_DOMAIN);
    if (!AWS.config.credentials) {
      var err = 'can not open cognito dataset: user AWS creds are null';
      logger.error (err);
      return callback(err, null);
    }

    AWS.config.credentials.identityId = getTempAWSCredsSubject();
    AWS.config.credentials.params = {
      IdentityPoolId: tempAWSCreds.cognitoIdentityPoolId
    };

    // instantiate sync manager with store in memory option
    // instead of default localStorage one
    var syncManager = new AWS.CognitoSyncManager( {
        DataStore: AWS.CognitoSyncManager.StoreInMemory
    });

    if (syncManager) {
      return syncManager.openOrCreateDataset(AWS_COGNITO_USER_STATE_DATASET,callback);
    }
    else {
      logger.error('failed to create AWS CognitoSyncManager');
    }
  }

  // retrieveFromCognitoDataset
  //---------------------------------------------------------------------------
//   function retrieveFromCognitoDataset(key,callback) {

//     openOrCreateCognitoDataset(function(err, dataset) {
//       if (err) {
//         logger.error ('read cognito open failed: ' + err);
//         return callback(err,null);
//       }

//       dataset.synchronize( {
//         onSuccess: function(dataset, newRecords) {
//           logger.debug('read cognito sync completed');
//           // get the updated info from local
//           dataset.get(key, function(err, value) {
//             if (!value || !value.length)
//               value = '{}';
//             logger.debug('read cognito record: ' + value);
//             return callback(null, JSON.parse(value));
//           });
//         },
//         onFailure: function(err) {
//           logger.error('read cognito sync failed:' + err);
//           return callback(err,null);
//         },
//         onConflict: function(dataset, conflicts, callback) {
//           var resolved = [];
//           for (var i = 0; i < conflicts.length; i++) {
//             // remote version wins in case of a conflict
//             resolved.push(conflicts[i].resolveWithRemoteRecord());
//           }
//           dataset.resolve(resolved, function() {
//             return callback(true);
//           });
//         }
//       });
//     });
//   }

  // retrieveAllFromCognitoDataset
  //---------------------------------------------------------------------------
//   function retrieveAllFromCognitoDataset(callback) {

//     openOrCreateCognitoDataset(function(err, dataset) {
//       if (err) {
//         logger.error ('read all cognito open failed: ' + err);
//         return callback(err,null);
//       }

//       dataset.synchronize( {
//         onSuccess: function(dataset, newRecords) {
//           logger.debug('read cognito sync completed');
//           // get the updated info from local
//           dataset.getAllRecords(function(err, records) {
//             var res = {};
//             logger.debug('read all cognito records: ' + records);
//             for (var i = 0; i < records.length; i++) {
//               if (records[i].value.length)
//                 res[records[i].key] = JSON.parse(records[i].value);
//             }
//             logger.debug('read all cognito records, transformed: ' +
//               JSON.stringify(res));
//             return callback(null, res);
//           });
//         },
//         onFailure: function(err) {
//           logger.error('read cognito sync failed:' + err);
//           return callback(err,null);
//         },
//         onConflict: function(dataset, conflicts, callback) {
//           var resolved = [];
//           for (var i = 0; i < conflicts.length; i++) {
//             // remote version wins in case of a conflict
//             resolved.push(conflicts[i].resolveWithRemoteRecord());
//           }
//           dataset.resolve(resolved, function() {
//             return callback(true);
//           });
//         }
//       });
//     });
//   }


  // saveToCognitoDataset
  //---------------------------------------------------------------------------
//   function saveToCognitoDataset(key,value,callback) {

//     openOrCreateCognitoDataset(function(err, dataset) {
//       if (err){
//         logger.error ('update cognito open failed: ' + err);
//         return callback(err,null);
//       }

//       dataset.put(key, JSON.stringify(value), function(err, record) {

//         if (err){
//           logger.error ('update cognito sync failed: ' + err);
//           return callback(err,null);
//         }

//         logger.debug('wrote cognito record:' + record);

//         dataset.synchronize({
//           onSuccess: function(dataset, newRecords) {
//             logger.debug('update cognito sync completed');
//             return callback(null,'success');
//           },
//           onFailure: function(err) {
//             logger.error ('update cognito sync failed: ' + err);
//             return callback(err,null);
//           },
//           onConflict: function(dataset, conflicts, callback) {
//             var resolved = [];
//             for (var i = 0; i < conflicts.length; i++) {
//               // local version wins in case of a conflict
//               resolved.push(conflicts[i].resolveWithLocalRecord());
//             }
//             dataset.resolve(resolved, function() {
//               return callback(true);
//             });
//           }
//         });
//       });
//     });
//   }

  // deleteFromCognitoDataset
  //---------------------------------------------------------------------------
//   function deleteFromCognitoDataset(key,callback) {

//     openOrCreateCognitoDataset(function(err, dataset) {
//       if (err){
//         logger.error ('delete cognito open failed: ' + err);
//         return callback(err,null);
//       }

//       dataset.remove(key, function(err, record) {

//         if (err){
//           logger.error ('delete cognito failed: ' + err);
//           return callback(err,null);
//         }

//         logger.debug('deleted cognito record key:' + key);

//         dataset.synchronize({
//           onSuccess: function(dataset, newRecords) {
//             logger.debug('delete cognito sync completed');
//             return callback(null,'success');
//           },
//           onFailure: function(err) {
//             logger.error ('delete cognito sync failed: ' + err);
//             return callback(err,null);
//           },
//           onConflict: function(dataset, conflicts, callback) {
//             var resolved = [];
//             for (var i = 0; i < conflicts.length; i++) {
//               // local version wins in case of a conflict
//               resolved.push(conflicts[i].resolveWithLocalRecord());
//             }
//             dataset.resolve(resolved, function() {
//               return callback(true);
//             });
//           }
//         });
//       });
//     });
//   }

  // getLegacyState
  //---------------------------------------------------------------------------
  function getLegacyState() {

    return currentLegacyState;
  }

  // getLocalTimeDifferenceInSecs
  //---------------------------------------------------------------------------
  function getLocalTimeDifferenceInSecs() {

    return diffWithLocalInSecs;
  }

  // executeAsEarlyAsPossible
  //---------------------------------------------------------------------------
  function executeAsEarlyAsPossible(func) {
    if (document.readyState === 'loading') { // page isn't loaded yet, so
      document.addEventListener('DOMContentLoaded', func) // run when loaded
    } else {
      func(); // run it right away (readyState is 'interactive' or 'complete')
    }
  }

  // hashCode
  //---------------------------------------------------------------------------
  function hashCode(str) {

    var hash = 0;
    if (!str || !str.length || str.length === 0) {
      return hash;
    }

    for (var i = 0; i < str.length; i++) {
      var char = str.charCodeAt(i);
      hash = ((hash << 5)-hash)+char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

//-----------------------------------------------------------------------------
function signRequest(catapultCBDomain, request) {

  let awsCredentials = getTempAWSCreds(catapultCBDomain);

  if (!awsCredentials) {
    throw `no valid temp catapult creds found for ${catapultCBDomain}`;
  }

  let options =  request instanceof Object ? request : { url:request };
  // canonical string must be upper case
  if (options.method)
    options.method = options.method.toUpperCase();

  options.accessKeyId = awsCredentials.accessKeyId;
  options.secretAccessKey = awsCredentials.secretAccessKey;
  options.sessionToken = awsCredentials.sessionToken;

  const awsSigner = new AwsV4Signer(options);

  return awsSigner.sign()
  .then (signedReq => {
    // add catapult headers (using set instead of append to overwrite)
    signedReq.headers.set(CATAPULT_AUTHN_HEADER_NAME,
      getAuthenticationToken());
    signedReq.headers.set(CATAPULT_AUTHZ_HEADER_NAME,
      getJWTToken());
    return signedReq;
  });
}

/**
 * 
 * @param {*} catapultCBDomain 
 * @param {*} requestOrURL (axios request object or url string for get):
 *   { 
        method: 'post',
        url: TEST_API_GW_POST_URL,
        timeout: 3000,
        data: {
          aString: 'bla',
          aNumber: 7
        }
      }
      OR
      'https://<your_api_gw_get_url>'

 */
function signAndSendAxiosCatapultRequest(catapultCBDomain, requestOrURL) {

  if (requestOrURL.data)
    requestOrURL.body = JSON.stringify(requestOrURL.data);

  return signRequest(catapultCBDomain, requestOrURL)
  .then(signedReq => {
    // transform into axios request object
    console.log(`signed request: ${JSON.stringify(signedReq)}`);
    let signedAxiosReq = {
      url:      signedReq.url,
      method :  signedReq.method,
      headers : {}
    };

    if (requestOrURL.data)
      signedAxiosReq.data = requestOrURL.data;

    for (let pair of signedReq.headers) {
      signedAxiosReq.headers[pair[0]] = pair[1];
    }

    return axios(signedAxiosReq);
  })
}

  // execute init
  init();

  /**
   * cb core iam module public API
   */

  export default {
    /**
     * CB IAM Events
     *
     * @property {object}  CB IAM events object
     *   {Login: "cb:core:iam:events:Login", Logout: "cb:core:iam:events:Logout", LegacyLoginLogout: "widgets:custom:LoginLogout"}
     */
    events : events,

    errorCodes: errorCodes,

    version : '8.1.0',

    /**
     * CB IAM Events Bus
     *
     * @returns Backbone.Events compatible CB IAM events bus
     */
    getEventBus : getEventBus,

    /**
     * init (mandatory)
     *
     * @param parameters object (optional)
     *    example: {eventBus: <backbone_events_bus>} (optional)
     */
    init : init,

    /**
     * getAuthSession
     *
     * @returns CB auth session object
     * example:
     * { isLoggedIn:true,
     *   sessionId:"6CCF86B2-1507-746F-EDAC-DDE2AE92B5E4", // or access token for Oauth sessions
     *   loginTokenType:"CBLogin",  // "Bearer" for Oauth sessions
     *   expireTimeInMS:1457730673453,
     *   basicProfile: {
     *     userName:"LEVPALM",
     *     firstName:"Lev",
     *     namespace:"st",
     *     isStudent:true,
     *     isProfessional:false
     *   }
     * }
     *
     */
    getAuthSession : getSession,

    /**
    * finalizeMyStyleURLWithEnv
    * replaces {{TREE-ENV}} with the current lower env if it exists
    *
    * @param url string that may contain {{TREE-ENV}} placeholder,
    *        e.g. https://public-nonprod.cbapis.org/{{TREE-ENV}}/persons/me/basicprofile...
    *
    * @returns a url string with the current env
    *        e.g. https://public-nonprod.cbapis.org/palm/persons/me/basicprofile...
    */
    finalizeMyStyleURLWithEnv : finalizeMyStyleURLWithEnv,

    /**
     * getLegacyState (for backward compatibility only)
     *
     * @deprecated since version 3.0
     *
     * @returns CB auth legacy state
     */
    getLegacyState : getLegacyState,

    getTempAWSCreds : getTempAWSCreds,

    getTempAWSCredsSubject : getTempAWSCredsSubject,

    getCatapultId : getTempAWSCredsSubject,

    getJWTToken : getJWTToken,

    getAuthenticationToken : getAuthenticationToken,

    getAuthorizationToken : getJWTToken,

    getCBEnv : adjustWithEnv,

    signAndSendAxiosCatapultRequest : signAndSendAxiosCatapultRequest,

    getLocalTimeDifferenceInSecs : getLocalTimeDifferenceInSecs,

    loginWithRAOTT : loginWithRAOTT,

    logoutWithRAOTT : logoutWithRAOTT,

    getActivityTrackerState : getActivityTrackerState,

    getHashId : getHashId

    // cognitoUserSessionDataHelper : {
    //   retrieve : retrieveFromCognitoDataset,
    //   retrieveAll: retrieveAllFromCognitoDataset,
    //   save: saveToCognitoDataset,
    //   delete: deleteFromCognitoDataset
    // }
  };